import {
  Component,
  Input,
  Optional,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, NavigationEnd, RouterEvent } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'suka-navigation-item',
  template: `
    <li [ngClass]="{
      'navigation__item': !isSubMenu,
      'navigation__menu-item': isSubMenu
    }"
    [attr.role]="(isSubMenu ? 'none' : null)">
      <a
        class="navigation__link"
        [href]="href"
        [attr.role]="(isSubMenu ? 'menuitem' : null)"
        [attr.aria-current]="(active ? 'page' : null)"
        (click)="navigate($event)">
        <div *ngIf="!isSubMenu" class="navigation__icon">
          <ng-content select="suka-icon"></ng-content>
        </div>
        <span class="navigation__link-text">
          <ng-content></ng-content>
        </span>
      </a>
    </li>
  `
})
export class NavigationItem implements OnDestroy {
  private subscription: Subscription;
  /**
   * Link for the item. NOTE: *do not* pass unsafe or untrusted values, this has the potential to open you up to XSS attacks
   */
  @Input() set href(v: string) {
    this._href = v;
  }

  get href() {
    return this.domSanitizer.bypassSecurityTrustUrl(this._href) as string;
  }

  /**
   * Toggles the active (current page) state for the link.
   */
  @Input() active = false;

  /**
   * Array of commands to send to the router when the link is activated
   * See: https://angular.io/api/router/Router#navigate
   */
  @Input() route: any[];

  /**
   * Router options. Used in conjunction with `route`
   * See: https://angular.io/api/router/Router#navigate
   */
  @Input() routeExtras: any;

  /**
   * Emits the navigation status promise when the link is activated
   */
  @Output() navigation = new EventEmitter<Promise<boolean>>();

  isSubMenu = false;

  protected _href = 'javascript:void(0)';

  constructor(protected domSanitizer: DomSanitizer, @Optional() protected router: Router) {
    if (router) {
      this.subscription = router.events.subscribe((s: RouterEvent) => {
        if (s instanceof NavigationEnd) {
          this.update();
        }
      });
    }
  }

  navigate(event): void {
    if (this.router && this.route) {
      event.preventDefault();
      const status = this.router.navigate(this.route, this.routeExtras);
      this.navigation.emit(status);
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private isActive(): boolean {
    const itemUrl = this.router.createUrlTree(this.route, this.routeExtras);
    return this.router.isActive(itemUrl, true);
  }

  private update(): void {
    if (this.isActive()) {
      this.active = true;
    } else {
      this.active = false;
    }
  }
}
